import dayjs from 'dayjs';

import { CommandTypeEnum } from '~enums';
import { getEmptyOffsetPagedResults, OffsetPagedResults } from '~interfaces';
import { SkcOffsetPagedResponse } from '~interfaces/responses';
import i18n from '~lib/i18n';
import ApiError from '~services/apiError';
import SkopeiConnectService from '~services/skopeiConnectService';

import TriggerSourceType from '../enum/triggerSourceType';
import TriggerTargetType from '../enum/triggerTargetType';
import Trigger from '../interfaces/trigger';

class TriggersService extends SkopeiConnectService {
	protected readonly path = 'triggers';

	/**
	 * Links a control box to a device.
	 *
	 * @param sourceDeviceId The device id of the source device which activates the trigger.
	 * @param targetDeviceId The device id of the target device on which the command is executed.
	 * @param triggerDo The trigger that's to be executed.
	 * @param deviceOrganisationId The organisation the device belongs to when performing this action as super admin.
	 * @param metadata Metadata value. For example the duration in minutes that office mode is enabled when sending enableOfficeMode command.
	 */
	async linkControlBox(params: {
		sourceDeviceId: string;
		targetDeviceId: string;
		triggerDo: string;
		deviceOrganisationId?: string | undefined;
		metadata?: number;
	}): Promise<void> {
		const endpoint = this.createEndpoint('v2');
		const name = `${params.sourceDeviceId}_${params.targetDeviceId}_${params.triggerDo}_${dayjs().format('DD/MM/YYYY_HH:mm:ss')}`;
		return await this.client
			.post(
				endpoint,
				{
					type: 'DeviceTrigger',
					name: name,
					deviceId: params.sourceDeviceId,
					target: {
						id: params.targetDeviceId,
						type: 'DeviceTarget',
					},
					triggerDo: params.triggerDo,
					triggerOn: 'IdAccessRequest',
					metadata: params.metadata,
				},
				{
					headers: {
						OverwrittenClientId: params.deviceOrganisationId,
					},
				}
			)
			.then(() => {
				return Promise.resolve();
			})
			.catch((error: ApiError) => {
				if (error.errorCode === 'SKC_011') {
					error.setDescription(i18n.t('linkControlBoxTypeMismatch', { ns: 'errors' }));
				}
				return Promise.reject(error);
			});
	}

	/**
	 * Gets a list of triggers.
	 *
	 * @param page The page.
	 * @param pageSize The page size.
	 * @param sourceType A TriggerSourceType to filter on.
	 * @param sourceId Filter on the device id of the device which activates the trigger.
	 * @param targetType A TriggerTargetType to filter on.
	 * @param targetId Filter on the device id of the target device on which the command is executed.
	 * @param deviceOrganisationId The organisation the device belongs to when performing this action as super admin.
	 * @param resourceGroupId A filter on triggers within a resource group.
	 * @param signal Signal for aborting the call.
	 *
	 * @returns An array of Triggers
	 */
	async getTriggers(
		params: {
			pageNumber: number;
			pageSize: number;
			sourceType?: TriggerSourceType;
			sourceId?: string;
			targetType?: TriggerTargetType;
			targetId?: string;
			deviceOrganisationId?: string | undefined;
			resourceGroupId?: string;
		},
		signal?: AbortSignal
	): Promise<OffsetPagedResults<Trigger>> {
		const endpoint = this.createEndpoint('v2');
		return await this.client
			.get<SkcOffsetPagedResponse<TriggerResponse>>(endpoint, {
				signal: signal,
				params: {
					'page-number': params.pageNumber ?? 1,
					'page-size': params.pageSize ?? 10,
					'source-type': params.sourceType,
					'source-id': params.sourceId,
					'resource-group-id': params.resourceGroupId,
					'target-type': params.targetType,
					'target-id': params.targetId,
				},
				headers: {
					OverwrittenClientId: params.deviceOrganisationId,
				},
			})
			.then((response) => {
				if (!response.data) {
					return Promise.resolve(
						getEmptyOffsetPagedResults<Trigger>(Number(params.pageNumber), Number(params.pageSize))
					);
				}
				return Promise.resolve(
					this.mapSkcOffsetPagedResponse(response.data, TriggersService.fromResponse)
				);
			});
	}

	public getTriggersCacheKey(
		pageNumber: number,
		pageSize: number,
		sourceType?: TriggerSourceType,
		sourceId?: string,
		targetType?: TriggerTargetType,
		targetId?: string,
		deviceOrganisationId?: string | undefined,
		resourceGroupId?: string
	) {
		return [
			this.path,
			pageNumber,
			pageSize,
			sourceType,
			sourceId,
			targetType,
			targetId,
			deviceOrganisationId,
			resourceGroupId,
		] as const;
	}

	/**
	 * Updates a trigger.
	 *
	 * @param triggerId The id of the trigger that's to be updated.
	 * @param sourceDeviceId The device id of the source device which activates the trigger.
	 * @param targetDeviceId The device id of the target device on which the command is executed.
	 * @param triggerDo The trigger that's to be executed.
	 * @param deviceOrganisationId The organisation the device belongs to when performing this action as super admin.
	 * @param metadata Metadata value. For example the duration in minutes that office mode is enabled when sending enableOfficeMode command.
	 *
	 * @returns
	 */
	async updateTrigger(
		triggerId: string,
		sourceDeviceId: string,
		targetDeviceId: string,
		triggerDo: string,
		deviceOrganisationId?: string | undefined,
		metadata?: number
	): Promise<void> {
		const endpoint = this.createEndpoint('v2', [triggerId]);
		return await this.client
			.put(
				endpoint,
				{
					type: 'DeviceTrigger',
					name: `${sourceDeviceId}_${targetDeviceId}_${triggerDo}_${dayjs().format('DD/MM/YYYY_HH:mm:ss')}`,
					deviceId: sourceDeviceId,
					target: {
						id: targetDeviceId,
						type: 'DeviceTarget',
					},
					triggerDo: triggerDo,
					triggerOn: 'IdAccessRequest',
					metadata: metadata,
				},
				{
					headers: {
						OverwrittenClientId: deviceOrganisationId,
					},
				}
			)
			.then(() => {
				return Promise.resolve();
			})
			.catch((error: ApiError) => {
				if (error.errorCode === 'SKC_011') {
					error.setDescription(i18n.t('linkControlBoxTypeMismatch', { ns: 'errors' }));
				}
				return Promise.reject(error);
			});
	}

	/**
	 * Deleted a trigger.
	 *
	 * @param triggerId The id of the trigger to delete.
	 * @param deviceOrganisationId The organisation the device belongs to when performing this action as super admin.
	 */
	async deleteTrigger(triggerId: string, deviceOrganisationId?: string | undefined): Promise<void> {
		const endpoint = this.createEndpoint('v2', [triggerId]);
		await this.client.delete(endpoint, {
			headers: {
				OverwrittenClientId: deviceOrganisationId,
			},
		});
	}

	/**
	 * Model parsing.
	 */
	static fromResponse(response: TriggerResponse): Trigger {
		const {
			id,
			type,
			metaData,
			triggerDo,
			target,
			deviceId,
			keyId,
			recurrenceScheduleId,
			...rest
		} = response;
		return {
			...rest,
			id: id.toString(),
			type: type as TriggerSourceType,
			triggerDo: triggerDo as CommandTypeEnum,
			metadata: metaData,
			target: {
				id: target.id.toString(),
				type: target.type as TriggerTargetType,
			},
			deviceId: deviceId?.toString(),
			keyId: keyId?.toString(),
			recurrenceScheduleId: recurrenceScheduleId?.toString(),
		};
	}
}

export interface TriggerResponse {
	type: string;
	id: number;
	name: string;
	triggerDo: string;
	metaData?: number;
	target: {
		type: string;
		id: number;
	};
	triggerOn: string;
	keyId?: number;
	deviceId?: number; // For type DeviceTrigger
	recurrenceScheduleId?: number; // For type ScheduleTrigger
}

export default TriggersService;
